import React from 'react';
import { cs } from '@bbkAdminUtils/css-utils';
import { DiscountLabel } from '@bbkPricingPage/components/card/components/discount-label';
import { Strikethrough } from '@bbkPricingPage/components/card/components/strikethrough';
import EqualHeightComponent from '@bbkPricingPage/components/equal-height-component/equal-height-component';
import {
  getAmountAfterDiscount,
  getMonthlyAmountBasedOnApplicability,
  getTotalAmount,
  priceMultiple,
} from '@bbkPricingPage/helpers/amount-calculator';
import { PricingTableHelper } from '@bbkPricingPage/helpers/pricing-table-helper';
import { getFormattedAmount } from '@bbkPricingPage/lib/currencies';
import {
  type Discount,
  type Frequency,
  type Item,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

type PriceComponentProps = {
  id: string;
  item?: Item;
  frequency: Frequency;
  price: Price;
  quantity: number;
  applicableDiscounts?: Discount[];
  frequencyOrientation?: 'col' | 'row';
  className?: string;
  priceClassName?: string;
  suffixClassName?: string;
  mandatoryAddonsPriceValue?: number;
  selectedPlanId?: string;
  ignoreEqualHeight?: boolean;
};

export const PriceComponent: React.FC<PriceComponentProps> = ({
  id,
  item,
  frequency,
  price,
  quantity,
  applicableDiscounts,
  frequencyOrientation = 'col',
  className,
  priceClassName,
  suffixClassName,
  mandatoryAddonsPriceValue,
  selectedPlanId,
  ignoreEqualHeight = false,
}) => {
  const isCustomAmountText = item?.customization.is_custom_amount_text;
  const customAmountText = item?.customization.custom_amount_text;
  const customAmountSubText = item?.customization.custom_amount_sub_text;
  quantity = quantity ?? 1;
  // Apply differential pricing if selectedPlanId is provided
  const effectivePrice = { ...price };
  if (selectedPlanId) {
    const applicableDifferentialPrice =
      effectivePrice.differential_prices?.find(
        (diffPrice) => diffPrice.billing_system_item_id === selectedPlanId
      );
    if (applicableDifferentialPrice) {
      effectivePrice.amount = applicableDifferentialPrice.amount;
      effectivePrice.tiers = applicableDifferentialPrice.tiers;
    }
  }

  const priceSuffix = PricingTableHelper.generatePriceSuffix(frequency);
  const totalAmount = getTotalAmount(price, quantity, 'amount');
  const totalDiscountedAmount = getAmountAfterDiscount(
    totalAmount,
    applicableDiscounts
  );
  const totalStrikethroughAmount = getTotalAmount(
    price,
    quantity,
    'strikethrough_amount'
  );

  // Apply multiplier if needed
  const multiplier = priceMultiple(
    frequency.period,
    frequency.period_unit,
    price.period,
    price.period_unit
  );

  // Calculate final amounts
  let amount = getMonthlyAmountBasedOnApplicability(
    totalDiscountedAmount * multiplier,
    frequency.period,
    frequency.period_unit,
    frequency.show_monthly_price
  );

  let strikethroughAmount = getMonthlyAmountBasedOnApplicability(
    totalStrikethroughAmount,
    frequency.period,
    frequency.period_unit,
    frequency.show_monthly_price
  );

  // Add mandatory addons if present
  if (mandatoryAddonsPriceValue) {
    amount += mandatoryAddonsPriceValue;
    strikethroughAmount += mandatoryAddonsPriceValue;
  }

  const formattedPriceAmount = getFormattedAmount(amount, price.currency);
  const formattedStrikeThroughAmount = getFormattedAmount(
    strikethroughAmount,
    price.currency
  );

  // Determine if strikethrough should be shown
  const hasDiscount = totalAmount !== totalDiscountedAmount;
  const hasStrikethrough = strikethroughAmount > amount;
  const showStrikethrough = hasDiscount || hasStrikethrough;

  const discountLabel = applicableDiscounts?.[0]?.label;

  return (
    <EqualHeightComponent
      groupId="dynamic-height-pricing-card-price"
      ignore={ignoreEqualHeight}
    >
      <div
        id={`item-price-group-${id}`}
        className={cs(
          'tw-flex tw-items-center tw-justify-center',
          isCustomAmountText || frequencyOrientation === 'col'
            ? 'tw-flex-col'
            : 'tw-flex-row tw-gap-1',
          className
        )}
      >
        <div
          id={`item-price-${id}`}
          className={cs(
            'tw-text-center tw-text-3xl tw-font-bold tw-tracking-tight !tw-text-[var(--pricing-page-text-font-text-color)]',
            priceClassName
          )}
        >
          {isCustomAmountText ? (
            customAmountText
          ) : showStrikethrough ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
              <Strikethrough
                id={id}
                amount={formattedPriceAmount}
                strikethroughAmount={formattedStrikeThroughAmount}
              />
              <DiscountLabel
                label={discountLabel}
                show={frequencyOrientation === 'col'}
              />
            </div>
          ) : (
            formattedPriceAmount
          )}
        </div>
        <div
          id={`frequency-priceSuffix-${frequency.period}-${frequency.period_unit}`}
          className={cs(
            'tw-text-center tw-text-base tw-font-medium',
            'tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]',
            suffixClassName
          )}
        >
          {isCustomAmountText ? customAmountSubText : priceSuffix}
        </div>
      </div>
    </EqualHeightComponent>
  );
};
